import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { createMask, InputMaskModule } from '@ngneat/input-mask';
import { TranslateModule } from '@ngx-translate/core';
import { FOREVER } from '@nk/utils-lib';
import * as dayjs from 'dayjs';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { ButtonDirective } from '../../../directives/button.directive';
import { FormControlValidPipe } from '../../../pipes/form-control-valid.pipe';

/**
 * Calendar-date control. Emits a local-midnight `Date`; serialize as
 * `'YYYY-MM-DD'` when sending — `Date.toISOString()` shifts the day west of UTC.
 */
@Component({
  selector: 'nk-date-control',
  imports: [
    TranslateModule,
    ButtonDirective,
    ReactiveFormsModule,
    BsDatepickerModule,
    InputMaskModule,
    FormControlValidPipe,
  ],
  templateUrl: './date-control.component.html',
  styleUrls: ['./date-control.component.scss'],
  providers: [
    FormControlValidPipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateControlComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateControlComponent implements ControlValueAccessor, AfterViewInit {
  public disabled = input(false);
  public id = input('');
  public title = input('');
  public invalid = input(false);
  public hideSubtitle = input(false);
  public showCustomButtons = input(false);
  public maxWidth = input<string>('');
  public size = input<'' | 'sm' | 'lg'>('');
  public formControl = new FormControl<Date | null>(null);
  public bsDatepickerFormControl = new FormControl<Date | null>(null);
  public dateInputMask = createMask<Date>({
    alias: 'datetime',
    inputFormat: 'dd.mm.yyyy',
    parser: (value: string) => {
      const values = value.split('.');
      const year = +values[2];
      const month = +values[1] - 1;
      const date = +values[0];
      return new Date(year, month, date);
    },
  });
  private _value: Date | null = null;
  public isDisabled = false;
  private _destroy = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      this.isDisabled = this.disabled();
      this._cdr.markForCheck();
    });
  }

  public ngAfterViewInit(): void {
    this._cdr.markForCheck();
    this.formControl.valueChanges
      .pipe(
        tap(() => {
          this._cdr.markForCheck();
        }),
        takeUntilDestroyed(this._destroy),
      )
      .subscribe((value) => {
        if (this.formControl.valid) {
          this.setValue(value);
        } else {
          this.setValue(null);
        }
      });

    this.bsDatepickerFormControl.valueChanges
      .pipe(
        tap(() => {
          this._cdr.markForCheck();
        }),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroy),
      )
      .subscribe((value) => {
        this.formControl.setValue(value);
      });
  }

  public setValue(value: Date | null): void {
    if (!this.disabled() && value !== this._value) {
      this._value = value;
      this.bsDatepickerFormControl.setValue(value);
      this.formControl.setValue(value);
      this.writeValue(value);
      this._cdr.markForCheck();
    }
  }

  public get value(): Date | null {
    return this._value;
  }

  public set value(value: Date | null) {
    if (value !== this._value) {
      this._value = value;
      this.onChange(value);
    }
  }

  public onChange = (value: Date | null): void => {
    if (value !== this._value) {
      this.setValue(value);
    }
  };

  public onTouched = (): void => {};

  public writeValue(value: Date | null): void {
    this.onChange(value);
    this.setValue(value);
  }

  public registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.isDisabled) {
      this.bsDatepickerFormControl.disable();
      this.formControl.disable();
    } else {
      this.bsDatepickerFormControl.enable();
      this.formControl.enable();
    }
    this._cdr.markForCheck();
  }

  public markAsTouched(): void {
    this.onTouched();
  }

  public setToday(): void {
    this.setValue(dayjs().startOf('day').toDate());
  }

  public setNever(): void {
    this.setValue(dayjs(FOREVER).toDate());
  }
}
